import { css } from "@emotion/react";
import type { CellContext } from "@tanstack/react-table";

const MAX_LENGTH = 80;

/**
 * Truncates the text if it is too long.
 * @param {string} text The text to truncate
 * @returns {string} The truncated text
 */
function formatText(text: string) {
  if (text.length > MAX_LENGTH) {
    return `${text.slice(0, MAX_LENGTH)}…`;
  }
  return text;
}

/**
 * A table cell that is designed to show text payload previews. It truncates long text,
 * provides the full string in a native tooltip, and uses monospace styling for clarity.
 */
export function TextCell<TData extends object, TValue>({
  getValue,
}: CellContext<TData, TValue>) {
  const value = getValue();
  if (value == null || value === "") {
    return <span css={css`color: #94a3b8; font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;`}>--</span>;
  }
  const fullStr = typeof value === "string" ? value : JSON.stringify(value);
  const displayStr = formatText(fullStr);
  const isTruncated = fullStr.length > MAX_LENGTH;

  return (
    <span
      title={fullStr}
      css={css`
        font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
        font-size: 12px;
        font-weight: 400;
        color: #334155;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
        max-width: 100%;
        cursor: ${isTruncated ? "help" : "default"};
        .theme--dark & {
          color: #cbd5e1;
        }
      `}
    >
      {displayStr}
    </span>
  );
}

/**
 * A table cell that shows pre-formatted text.
 */
export function PreformattedTextCell<TData extends object, TValue>({
  getValue,
}: CellContext<TData, TValue>) {
  const value = getValue();
  const str = value != null && typeof value === "string" ? value : "--";
  return (
    <pre
      title={str !== "--" ? str : undefined}
      css={css`
        white-space: pre-wrap;
        font-family: 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
        font-weight: 400;
        font-size: 12px;
        color: #334155;
        margin: 0;
        .theme--dark & {
          color: #cbd5e1;
        }
      `}
    >
      {str}
    </pre>
  );
}
